/* eslint-disable no-use-before-define */
import React from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import StructureModel from '@Components/Results/structure/structure-model.service';
import StructureZoomService from '@Services/StructureZoom';
import Structure from '@Components/Results/structure/Structure';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import classnames from 'classnames';
import SubstanceIdentificationChemicalNamesList from '@Components/Results/substances/SubstanceIdentificationChemicalNamesList';

import './SubstanceIdentificationData.scss';

const IDENTIFICATION_DATA_CLASS = 'rx-substance-identification';
const IDENTIFICATION_DATA_ID = 'rx-substance-identification';

export default function SubstanceIdentificationData({ metadata, database, details, zoom }) {
  const zoomClasses = StructureZoomService.getZoomClasses('substances') || [];
  const zoomClass = zoomClasses[zoom];
  return (
    <>
      {renderIdentificationTable(details, database.isCIDatabase, database.isCommercialSupplierData)}
      <div className="align-structures">{renderMixtureItems(details.mixtureItems, database.id, zoomClass, zoom, metadata)}</div>
    </>
  );
}

function renderDescriptionListEntry(label, value) {
  if (!value) {
    return null;
  }

  return (
    <>
      <dt className={`${IDENTIFICATION_DATA_CLASS}__term`}>{label}</dt>
      <InnerHTML tag="dd" className={`${IDENTIFICATION_DATA_CLASS}__description`} html={value} />
    </>
  );
}

function getSubstanceType(type, isCIDatabase) {
  if (isCIDatabase || isEmpty(type)) {
    return null;
  }

  return isArray(type) ? type.join(', ') : type;
}

function renderIdentificationIdRow(id, isCIDatabase, isCommercialSupplierData) {
  if (isCIDatabase) {
    return null;
  }

  return (
    <div className={`${IDENTIFICATION_DATA_CLASS}__row ${IDENTIFICATION_DATA_CLASS}__row--single-column`}>
      {renderDescriptionListEntry(isCommercialSupplierData ? 'Commercial Substances ID:' : 'Reaxys ID:', id)}
    </div>
  );
}

function renderRegistryRow(casRegistryNumbers, substanceType) {
  if (!casRegistryNumbers) {
    return null;
  }

  return (
    <div className={`${IDENTIFICATION_DATA_CLASS}__row registry-numbers-substance-row`}>
      {renderDescriptionListEntry('CAS Registry Number(s):', casRegistryNumbers)}
      {renderDescriptionListEntry('Substance type:', substanceType)}
    </div>
  );
}

function renderFormulaRow(formula, linearFormula, isCommercialSupplierData) {
  if (!formula && !linearFormula) {
    return null;
  }

  return (
    <div className={`${IDENTIFICATION_DATA_CLASS}__row formula-substance-row`}>
      {formula ? renderDescriptionListEntry('Molecular Formula:', formula) : null}
      {!isCommercialSupplierData ? renderDescriptionListEntry('Linear Structure Formula:', linearFormula) : null}
    </div>
  );
}

function renderCommercialLinearFormulaRow(linearFormula, isCommercialSupplierData) {
  if (!isCommercialSupplierData || !linearFormula) {
    return null;
  }

  return (
    <div
      className={`${IDENTIFICATION_DATA_CLASS}__row ${IDENTIFICATION_DATA_CLASS}__row--single-column commercial-supplier-data-substance-row`}>
      {renderDescriptionListEntry('Linear Structure Formula:', linearFormula)}
    </div>
  );
}

function renderWeightRow(weight, referencesCount, isCIDatabase) {
  if (!weight) {
    return null;
  }

  return (
    <div className={`${IDENTIFICATION_DATA_CLASS}__row weight-substance-row`}>
      {renderDescriptionListEntry('Molecular Weight:', weight)}
      {!isCIDatabase ? renderDescriptionListEntry('No of references:', referencesCount) : null}
    </div>
  );
}

function renderSingleColumnRow(label, value) {
  if (!value) {
    return null;
  }

  return (
    <div className={`${IDENTIFICATION_DATA_CLASS}__row ${IDENTIFICATION_DATA_CLASS}__row--single-column`}>
      {renderDescriptionListEntry(label, value)}
    </div>
  );
}

function renderIdentificationTable(details, isCIDatabase, isCommercialSupplierData) {
  const { id, type = [], formula, linearFormula, weight, referencesCount, modification, inChiKey, chemicalNames } = details;
  const casRegistryNumbers = (details.casRegistryNumbers || []).join(', ');
  const substanceType = getSubstanceType(type, isCIDatabase);

  return (
    <dl className={`${IDENTIFICATION_DATA_CLASS} results-key-value-wrapper u-els-padding-left-2x`} id={IDENTIFICATION_DATA_ID}>
      <SubstanceIdentificationChemicalNamesList chemicalNames={chemicalNames} />
      {renderIdentificationIdRow(id, isCIDatabase, isCommercialSupplierData)}
      {renderRegistryRow(casRegistryNumbers, substanceType)}
      {renderFormulaRow(formula, linearFormula, isCommercialSupplierData)}
      {renderCommercialLinearFormulaRow(linearFormula, isCommercialSupplierData)}
      {renderWeightRow(weight, referencesCount, isCIDatabase)}
      {renderSingleColumnRow('Modification:', modification)}
      {renderSingleColumnRow('InChIKey:', inChiKey)}
    </dl>
  );
}

function renderMixtureItems(items, databaseId, zoomClass, zoom, metadata = {}) {
  return map(items, (structure, index) => (
    <div className={classnames('mixture-structure-wrapper', zoomClass)} key={index}>
      <span className="structure-content-label">
        {structure.name} {structure.amount ? `(${structure.amount})` : ''}
      </span>
      <div className="mixture-structure mixture-structure--react">
        <Structure metadata={metadata} structure={formatStructure(structure, databaseId)} zoom={zoom} />
      </div>
    </div>
  ));
}

function formatStructure(structure, databaseId) {
  // eslint-disable-next-line no-param-reassign
  structure.databaseId = databaseId;
  return new StructureModel(structure);
}

SubstanceIdentificationData.propTypes = {
  metadata: MetadataPropTypesShape,
  details: PropTypes.shape({
    id: PropTypes.string,
    chemicalNames: PropTypes.array,
    casRegistryNumbers: PropTypes.array,
    type: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    formula: PropTypes.string,
    linearFormula: PropTypes.string,
    weight: PropTypes.string,
    referencesCount: PropTypes.string,
    modification: PropTypes.string,
    inChiKey: PropTypes.string,
    mixtureItems: PropTypes.array,
  }).isRequired,
  database: PropTypes.object.isRequired,
  zoom: PropTypes.number,
};
